import { useEffect, useRef, useState } from 'react';
// import { format, isValid, parse } from 'date-fns';
import { DayPicker } from 'react-day-picker';
import { es } from 'react-day-picker/locale';
import { useDispatch, useSelector } from 'react-redux';
import { getAllReservations } from '../redux/slices/reservationSlice';
import { useTranslation } from 'react-i18next';

import './calendar-picker.css';
const CalendarDialog = ({ value, onChange, equipmentId, quantity = 1, totalInstances = 0 }) => {
  const { i18n, t } = useTranslation();
  const dialogRef = useRef(null);
  const dispatch = useDispatch();
  const currentLang = i18n.language;

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedRange, setSelectedRange] = useState(value);
  const [availabilityByDate, setAvailabilityByDate] = useState({});

  const reservations = useSelector((state) => state.reservations.reservations);

  useEffect(() => {
    if (!value || !value.from) {
      setSelectedRange(undefined);
    } else {
      setSelectedRange(value);
    }
  }, [value]);

  useEffect(() => {
    if (dialogRef.current) {
      if (isDialogOpen) {
        dialogRef.current.showModal();
      } else {
        dialogRef.current.close();
      }
    }
  }, [isDialogOpen]);

  //get reservations for each type of equipment
  useEffect(() => {
    if (typeof equipmentId === 'string' && equipmentId.trim() !== '') {
      dispatch(getAllReservations(equipmentId));
    }
  }, [equipmentId, dispatch]);

  // Calculate availability by date based on reservations
  useEffect(() => {
    if (!reservations || reservations.length === 0 || totalInstances === 0) {
      setAvailabilityByDate({});
      return;
    }

    const availability = {};

    // For each reservation, mark dates as having one less available
    reservations.forEach((res) => {
      if (
        !res ||
        !res.startDate ||
        !res.endDate ||
        res.reservationStatus?.toLowerCase() === 'canceled' ||
        res.reservationStatus?.toLowerCase() === 'returned'
      ) {
        return;
      }

      const start = new Date(res.startDate);
      const end = new Date(res.endDate);

      // Normalize to midnight local time
      start.setHours(0, 0, 0, 0);
      end.setHours(0, 0, 0, 0);

      const quantity = res.quantity || 1;

      if (isNaN(start) || isNaN(end)) return;

      let d = new Date(start);
      while (d <= end) {
        const dateKey = d.toISOString().split('T')[0];
        availability[dateKey] = (availability[dateKey] || 0) + quantity;
        d.setDate(d.getDate() + 1);
      }
    });

    setAvailabilityByDate(availability);
  }, [reservations, totalInstances]);

  const toggleDialog = () => {
    setIsDialogOpen((prev) => !prev);
  };

  const handleDayPickerSelect = (range) => {
    // If no range is selected, do nothing
    //if (!range) return;

    // If user clicks the same start date again, clear the selection
    if (
      selectedRange?.from &&
      !selectedRange.to &&
      range.from &&
      selectedRange.from.getTime() === range.from.getTime()
    ) {
      setSelectedRange(undefined);
      onChange(undefined);
      return;
    }

    // Otherwise, update the selection normally
    setSelectedRange(range);
    onChange(range);
  };

  const handleDialogClick = (e) => {
    e.stopPropagation();
  };

  // Get dates where the requested quantity is NOT available
  const getUnavailableDates = () => {
    const unavailable = [];

    if (totalInstances === 0) return unavailable;

    // Check each date in availabilityByDate
    Object.keys(availabilityByDate).forEach((dateKey) => {
      const reservedCount = availabilityByDate[dateKey];
      const availableCount = totalInstances - reservedCount;

      // If available count is less than requested quantity, mark as unavailable
      if (availableCount < quantity) {
        // Parse date in local timezone to avoid UTC issues
        const [year, month, day] = dateKey.split('-').map(Number);
        const date = new Date(year, month - 1, day);
        unavailable.push(date);
      }
    });

    return unavailable;
  };

  // Sets disabled dates to before current date and unavailable dates
  const getDisabledDates = () => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const disabled = [{ before: today }];
    const unavailable = getUnavailableDates();

    if (
      !selectedRange ||
      !selectedRange.from ||
      selectedRange.from === null ||
      isNaN(new Date(selectedRange.from).getTime())
    ) {
      return [...disabled, ...unavailable];
    }

    const startDate = new Date(selectedRange.from);
    startDate.setHours(0, 0, 0, 0);

    // Find the next unavailable date after the start date
    const nextUnavailable = unavailable
      .filter((date) => {
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        return checkDate > startDate;
      })
      .sort((a, b) => a - b)[0];

    if (nextUnavailable) {
      // Disable all dates from (and including) the next unavailable date onwards
      const dayBeforeUnavailable = new Date(nextUnavailable);
      dayBeforeUnavailable.setDate(dayBeforeUnavailable.getDate() - 1);
      return [...disabled, ...unavailable, { after: dayBeforeUnavailable }];
    }
    return [...disabled, ...unavailable];
  };

  return (
    <div>
      <button
        className="border-2 border-lime-700 text-lime-700 px-4 py-1 rounded-full hover:bg-lime-50 text-md"
        onClick={toggleDialog}
      >
        {t('farmshare.calendarDialog.selectDateRange')}
      </button>
      <dialog ref={dialogRef} onClick={toggleDialog} className="border-2 rounded-lg">
        <div className="p-4 flex justify-center items-center flex-col" onClick={handleDialogClick}>
          {totalInstances > 0 && (
            <div className="mb-2 text-sm text-gray-600 text-center">
              Total: {totalInstances} unit{totalInstances !== 1 ? 's' : ''} available
            </div>
          )}
          <DayPicker
            mode="range"
            numberOfMonths={2}
            selected={selectedRange}
            onSelect={handleDayPickerSelect}
            disabled={getDisabledDates()}
            locale={currentLang === 'es' ? es : undefined}
          />
          <button
            className="mb-4 p-2 bg-green-700 text-white rounded hover:bg-green-800"
            onClick={() => setIsDialogOpen(false)}
          >
            {t('farmshare.calendarDialog.confirmDates')}
          </button>
        </div>
      </dialog>
    </div>
  );
};

export { CalendarDialog };
